import { html, TemplateResult } from 'lit';
import { property, query, state } from 'lit/decorators.js';

import Dropdown from '~/ui/components/dropdown';
import '~/ui/components/dropdown';

import { SalesSite, SalesSiteDropDownItem } from './types';
import DERFormElement from '~/ui/utils/der-form-element';

export class SalesSites extends DERFormElement {
    @property({ type: String, attribute: 'label' })
    label: string = '';

    @property({ type: String, attribute: 'lang' })
    override lang: string = '';

    @property({ type: String, attribute: 'name' })
    name: string = '';

    @property({ type: String, attribute: 'placeholder' })
    placeholder: string = '';

    @property({ type: Boolean, attribute: 'required' })
    required: boolean = false;

    @property({ type: String, attribute: 'sales-site-url' })
    salesSiteUrl: string = '';

    @property({ type: String, attribute: 'selected-team-id' })
    selectedTeamId: string = '';

    @state()
    private salesSitesData: SalesSite[] = [];

    @query('der-ui-dropdown') dropdownElement?: Dropdown;

    /**
     * Sets up shadow DOM with delegated focus.
     */
    constructor() {
        super();
        this.attachShadow({
            mode: 'open',
            delegatesFocus: true,
        });
    }

    /**
     * Lifecycle: fetches data and validates after initial render.
     */
    override connectedCallback(): void {
        super.connectedCallback();
        this._getSalesSitesData();
        this.updateComplete.then(() => {
            this._checkInputValidity();
        });
    }

    /**
     * Reacts to changes in lang or salesSiteUrl to refetch data.
     * @param _changedProperties Map of changed properties.
     */
    override async updated(_changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
        if ((_changedProperties.has('lang') || _changedProperties.has('salesSiteUrl')) && this.salesSiteUrl) {
            await this._getSalesSitesData();
        }
    }

    /**
     * Fetches sales site data from configured endpoint.
     * Sets error state on failure.
     */
    private async _getSalesSitesData(): Promise<void> {
        if (!this.salesSiteUrl) {
            this.salesSitesData = [];
            return;
        }

        const url: URL = new URL(this.salesSiteUrl);
        url.searchParams.set('lang', this.lang ?? '');

        const response: Response = await fetch(url.toString());
        if (!response.ok) {
            this.error(this.tagName, {
                message: 'Can not load sales-sites data',
            });
            this.salesSitesData = [];
            return;
        }
        const data: unknown = await response.json();
        this.salesSitesData = data as SalesSite[];
    }

    /**
     * Transforms raw data into dropdown items.
     * @returns Array of dropdown option objects.
     */
    get dropdownItems(): SalesSiteDropDownItem[] {
        if (!Array.isArray(this.salesSitesData) || !this.salesSitesData.length) return [];
        return this.salesSitesData
            .filter((item: SalesSite): boolean => !!item.teamId)
            .map((item: SalesSite): SalesSiteDropDownItem => {
                const { zip, address, city, name } = item;
                const parts: unknown[] = [zip, city, address, name].filter(Boolean);
                return {
                    name: parts.join(' - '),
                    id: item.teamId,
                };
            });
    }

    /**
     * Handles dropdown change event.
     * Updates form value and emits sales-site-selected.
     * @param event Custom event with new value.
     */
    private _handleChange(event: CustomEvent<{ value: string }>): void {
        event.stopPropagation();
        this.selectedTeamId = event.detail.value;
        this._internals.setFormValue(this.selectedTeamId);
        this._checkInputValidity();
        /**
         * Dispatches 'sales-site-selected' with the selected team id in detail.
         * detail.salesSite = selectedTeamId
         */
        this.dispatchEvent(
            new CustomEvent('sales-site-selected', {
                detail: {
                    salesSite: this.selectedTeamId,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Runs after dropdown signals render completion to sync validity.
     */
    private _handleRendered(): void {
        this._checkInputValidity();
    }

    /**
     * Copies validity from internal select element into DER form system.
     */
    private _checkInputValidity(): void {
        if (!this.dropdownElement?.selectEl) {
            return;
        }
        this.setDERValidity(
            this.dropdownElement.selectEl.validity,
            this.dropdownElement.selectEl.validationMessage,
            this.dropdownElement.selectEl
        );
    }

    /**
     * Renders the dropdown with computed items.
     * @returns TemplateResult for component UI.
     */
    override render(): TemplateResult {
        return html`
            <der-ui-dropdown
                label="${this.label}"
                name="${this.name}"
                @der.change=${this._handleChange}
                @rendered=${this._handleRendered}
                .options=${this.dropdownItems}
                placeholder="${this.placeholder}"
                ?required="${this.required}"
            ></der-ui-dropdown>
        `;
    }
}
